import { PaymentService } from '../../shared/services/payment.service';
import { Router, ActivatedRoute } from '@angular/router';
import {
  Component,
  OnInit,
  ChangeDetectorRef,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { AuthenticationService, ApiService } from '../../shared/services';

import { trigger, style, animate, transition } from '@angular/animations';

import { ProgressSpinnerService } from '../../shared/services/progress-spinner.service';

import {
  LoginMediaItem,
  ThemeMediaTypeEnum,
} from '../user-dashboard/portal/portal.model';
import { cardAnimation } from '../../shared/animations/animations';
import { AppwriteService } from '../../shared/services/appwrite.sdk.service';

import { AnimatedLoaderType } from 'src/app/shared/models/animated-loader-type.enum';
import { environment } from 'src/environments/environment';
import { BetaInvitationValidationResponse } from 'src/app/models/register';
import { lastValueFrom, throwError } from 'rxjs';

export enum PaymentMethodEnum {
  none,
  stripe,
  btcpay,
}

export enum RegisterErrorType {
  none,
  username_exist,
  email_exist,
}

export enum PaymentProcessType {
  save_and_register,
  save_only,
  pay_and_save,
  just_pay,
}

@Component({
  templateUrl: './register.component.html',
  styleUrls: [
    './register.component.css',
    '../profile/payments/payments.component.css',
  ],
  animations: [
    cardAnimation,
    trigger('inOutSlide', [
      transition(':enter', [
        style({ maxHeight: '0px', minHeight: '0', opacity: 0 }),
        animate('0.3s ease-out', style({ maxHeight: '200px', opacity: 1 })),
      ]),
      transition(':leave', [
        style({ maxHeight: '200px', opacity: 1 }),
        animate(
          '0.2s ease-out',
          style({ maxHeight: '0px', minHeight: '0', opacity: 0 }),
        ),
      ]),
    ]),
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: '0' }),
        animate('0.5s ease-out', style({ opacity: '1' })),
      ]),
    ]),
    trigger('text-animation', [
      transition(':enter', [
        style({ maxHeight: '0px', minHeight: '0', opacity: 0 }),
        animate('0.6s ease-out', style({ maxHeight: '200px', opacity: 1 })),
      ]),
    ]),
  ],
})
export class RegisterComponent implements OnInit {
  animatedLoaderType = AnimatedLoaderType;

  _images: LoginMediaItem[] = [
    {
      title: 'Create',
      content: `Learmo enables you to craft various digital educational materials, such as digital books and coaching programs, to showcase your expertise.`,
      image: 'assets/images/svg/system/login-features/create.svg',
      type: ThemeMediaTypeEnum.image,
      mainColor: '#0963f6',
    },
    {
      title: 'Grow',
      content: `Learmo offers a comprehensive range of marketing tools and templates, allowing you to quickly generate new content, launch campaigns, and set up sales funnels.`,
      image: 'assets/images/svg/system/login-features/grow.svg',
      type: ThemeMediaTypeEnum.image,
      mainColor: '#FFC727',
    },
    {
      title: 'Manage',
      content: `Effectively manage your educational growth with Learmo's integrated CRM system, accept payments through BTC, Stripe, and PayPal integrations, and leverage analytics to make informed business decisions.`,
      image: 'assets/images/svg/system/login-features/manage.svg',
      type: ThemeMediaTypeEnum.image,
      mainColor: '#92E3A9',
    },
  ];

  registerform = new UntypedFormGroup({
    fullName: new UntypedFormControl('fullname'),
    email: new UntypedFormControl('email'),
    username: new UntypedFormControl('username'),
    password: new UntypedFormControl('password'),
  });

  loading: boolean = false;
  /** When invitation gate is on, hide the form until validation succeeds. */
  invitationReady: boolean = !environment.betaInvitationRequired;
  invitation: BetaInvitationValidationResponse | null = null;

  isHasNumber: boolean = false;
  isContainCases: boolean = false;

  constructor(
    private appwriteService: AppwriteService,
    private fb: UntypedFormBuilder,
    private readonly authenticationService: AuthenticationService,
    private readonly api: ApiService,
    private readonly router: Router,
    private readonly aroute: ActivatedRoute,
    public loadingService: ProgressSpinnerService,
    public paymentService: PaymentService,
    public cdr: ChangeDetectorRef,
  ) {}

  async ngOnInit() {
    if (!!this.authenticationService.currentUserValue) {
      this.router.navigate(['dashboard']);
      return;
    }

    this.appwriteService.clearAppwriteSession();

    if (environment.betaInvitationRequired) {
      await this.handleBetaInvitationGate();
    }
  }

  private async handleBetaInvitationGate() {
    const token = this.aroute.snapshot.queryParamMap.get('token');

    if (!token) {
      this.router.navigate(['/invitation-needed']);
      return;
    }

    this.loading = true;
    this.invitationReady = false;

    try {
      const response = (await lastValueFrom(
        this.authenticationService.validateBetaInvitation(
          token,
          this.handleInvitationValidationError,
        ),
      )) as BetaInvitationValidationResponse;

      this.invitation = response;
      this.invitationReady = true;
    } catch {
      // Error handler navigates away; keep form hidden.
      this.invitationReady = false;
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  private handleInvitationValidationError = (error: {
    error?: string;
    message?: string;
    statusCode?: number;
  }) => {
    this.router.navigate(['/invitation-needed']);
    return throwError(() => error);
  };

  scroll(el: HTMLElement) {
    el.scrollIntoView();
  }

  onSignupStart() {
    this.loading = true;
  }

  onSignupDone() {
    this.loading = false;
  }

  onSignupError() {
    this.loading = false;
  }

  passwordUIValidator(value: string) {
    this.isHasNumber = this.hasNumber(value);
    this.isContainCases = this.containCases(value);
  }

  hasNumber(value: string) {
    if (/\d/.test(value)) {
      return true;
    } else {
      return false;
    }
  }

  containCases(value: string) {
    if (
      /^(?=.*?[A-Z])(?=.*?[a-z])[A-Za-z\/\]\}\{\d~`!@#\$%\^&\*\(\)_\-\+={\[\}\]\|\\:;"'<,>\.\?\/ ]{0,}$/.test(
        value,
      )
    ) {
      return true;
    } else {
      return false;
    }
  }
}
